import React from 'react';
import {
    Box,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    NumberInput,
    NumberInputField,
    Radio,
    RadioGroup,
    Text,
    Textarea,
    VStack,
} from '@chakra-ui/react';
import type { PxAnswer, PxQuestion } from '~/meta/data/px-connect/types';
import {
    displayQuantity,
    isQuantityCharacter,
    parseQuantity,
    parseQuantityInput,
    toQuantityInput,
} from '~/meta/data/px-connect';
import type { PxReportAction } from '~/reducers/pxReportReducer';

/**
 * Obergrenze je Kommentar (UCPWO-410). PX Connect selbst gibt keine vor; die Grenze kommt
 * aus `Projekt.metadata` (varchar(5000)), in der der ganze Entwurf liegt. Ein Katalog hat
 * rund 10 Kommentarfelder, neben Antworten, Terminen und Versand bleiben dafür ~2000 Zeichen.
 */
export const PX_KOMMENTAR_MAX = 200;

/**
 * Data-driven recursive renderer for the PX Connect Fragenkatalog (UCPWO-378).
 *
 * Renders a list of questions by key and reveals follow-up questions inline as
 * answers are selected (`relatedQuestions`). Container-agnostic: it takes the
 * catalog + a dispatch and knows nothing about its host (modal vs page).
 * Dangling `relatedQuestions` (keys absent from the catalog) are skipped.
 */

/**
 * Internal identity of an answer: the uid assigned on INIT (see assignAnswerUids),
 * falling back to the API key. Never the bare API `key`, which is not unique
 * within a question.
 */
const answerUidOf = (answer: PxAnswer): string => answer.uid ?? answer.key;

interface PxQuestionListProps {
    /** The full catalog — used to resolve `relatedQuestions` by key. */
    questions: PxQuestion[];
    /** Which question keys to render at this level. */
    questionKeys: string[];
    dispatch: React.Dispatch<PxReportAction>;
    /** Keys of mandatory questions flagged invalid on submit (UCPWO-378). */
    errorKeys?: Set<string>;
    /** Nesting depth, drives indentation. */
    depth?: number;
    /**
     * Sperrt alle Antworten, solange der Entwurf gespeichert bzw. die Korrespondenz
     * vorbereitet wird (UCPWO-420). Beides arbeitet mit dem Stand beim Klick; was danach
     * noch geändert würde, ginge beim Schließen des Dialogs stillschweigend verloren.
     */
    isDisabled?: boolean;
}

const PxAnswerReveals: React.FC<{
    question: PxQuestion;
    answer: PxAnswer;
    questions: PxQuestion[];
    dispatch: React.Dispatch<PxReportAction>;
    errorKeys?: Set<string>;
    depth: number;
    isDisabled?: boolean;
}> = ({ question, answer, questions, dispatch, errorKeys, depth, isDisabled }) => {
    // Rohtext der Mengeneingabe in Chakras *Eingabeform* (Punkt als Dezimaltrenner, siehe
    // quantity.ts). Nötig, weil ein rein aus `answer.quantity` abgeleiteter Wert das gerade
    // getippte Komma sofort wieder wegformatieren würde („2," → 2 → „2").
    const [quantityInput, setQuantityInput] = React.useState(() =>
        toQuantityInput(answer.quantity)
    );

    // Von außen gesetzte Mengen übernehmen (INIT / APPLY_DRAFT beim Fortsetzen eines
    // Entwurfs), ohne die laufende Eingabe zu zerstören: „2," parst zu 2 und darf deshalb
    // nicht auf „2" zurückgeschrieben werden.
    React.useEffect(() => {
        if (parseQuantity(quantityInput) !== answer.quantity) {
            setQuantityInput(toQuantityInput(answer.quantity));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answer.quantity]);

    // Nur rendern, wenn es etwas zu zeigen gibt — ein leerer Container hinterlässt sonst
    // sein `mt` als sichtbaren Freiraum unter genau der angeklickten Antwort.
    const hasReveal =
        answer.withComment ||
        answer.withQuantity ||
        Boolean(answer.relatedQuestions && answer.relatedQuestions.length > 0);
    if (!answer.isSelected || !hasReveal) return null;
    return (
        <VStack align="stretch" spacing={3} pl={6} mt={2} data-test-id="px-answer-reveal">
            {answer.withComment && (
                <Box>
                    <Textarea
                        size="sm"
                        maxLength={PX_KOMMENTAR_MAX}
                        placeholder="Bemerkung"
                        value={answer.comment ?? ''}
                        onChange={(event) =>
                            dispatch({
                                type: 'SET_COMMENT',
                                questionKey: question.key,
                                answerUid: answerUidOf(answer),
                                comment: event.target.value,
                            })
                        }
                    />
                    <Text
                        fontSize="xs"
                        textAlign="right"
                        color={
                            (answer.comment?.length ?? 0) >= PX_KOMMENTAR_MAX ? 'red.500' : 'gray.500'
                        }
                        data-test-id="px-comment-counter"
                    >
                        {answer.comment?.length ?? 0} / {PX_KOMMENTAR_MAX}
                    </Text>
                </Box>
            )}
            {answer.withQuantity && (
                <HStack>
                    <NumberInput
                        size="sm"
                        min={0}
                        maxW={32}
                        value={quantityInput}
                        // Komma erlauben (Chakras Default-Filter lässt nur `.` durch).
                        //
                        // `parse`/`format` müssen ein reines String-Paar sein, das sich exakt
                        // umkehrt: Chakra rechnet intern mit der geparsten Form und zeigt
                        // `format(intern)` an. Deshalb hier nur Zeichentausch (auf Modulebene,
                        // damit die Identitäten über Re-Renders stabil bleiben), und
                        // `toQuantityInput` (numerisch) ausschließlich fürs Vorbelegen/Blur.
                        isValidCharacter={isQuantityCharacter}
                        parse={parseQuantityInput}
                        format={displayQuantity}
                        // Chakras Blur-Clamp castet den Wert über die interne `parse` des
                        // Counters, die unsere `parse`-Prop ignoriert und das Komma entfernt
                        // (`/[^\w.-]+/g`) — aus „10,45" wurde `1045`. Wir normalisieren beim
                        // Blur selbst; min/max wird ohnehin von `parseQuantity` erzwungen.
                        clampValueOnBlur={false}
                        onChange={(valueAsString) => {
                            setQuantityInput(valueAsString);
                            dispatch({
                                type: 'SET_QUANTITY',
                                questionKey: question.key,
                                answerUid: answerUidOf(answer),
                                quantity: parseQuantity(valueAsString),
                            });
                        }}
                    >
                        {/* Erst beim Verlassen des Feldes numerisch normalisieren („2," → „2",
                            „2,567" → „2,56"); während des Tippens bleibt der Rohtext stehen.
                            Das deutsche Komma macht durchgehend die `format`-Prop. */}
                        <NumberInputField
                            onBlur={() =>
                                setQuantityInput(toQuantityInput(parseQuantity(quantityInput)))
                            }
                        />
                        {/* KEINE Stepper-Pfeile: Chakras Stepper liest den Anzeigewert über seine
                            eigene Sanitisierung, die das Komma *entfernt* statt es als Trenner zu
                            nehmen — aus „2,56" wurde per Klick „256,5" (auf staging verifiziert).
                            Für Flächen in qm tippt man den Wert ohnehin. */}
                    </NumberInput>
                    {answer.quantityDescription && (
                        <Text fontSize="sm" color="gray.600">
                            {answer.quantityDescription}
                        </Text>
                    )}
                </HStack>
            )}
            {answer.relatedQuestions && answer.relatedQuestions.length > 0 && (
                <PxQuestionList
                    questions={questions}
                    questionKeys={answer.relatedQuestions}
                    dispatch={dispatch}
                    errorKeys={errorKeys}
                    depth={depth + 1}
                    isDisabled={isDisabled}
                />
            )}
        </VStack>
    );
};

const PxAnswerHint: React.FC<{ hint: string | null }> = ({ hint }) =>
    hint ? (
        <Text fontSize="xs" color="gray.500" pl={6}>
            {hint}
        </Text>
    ) : null;

const PxQuestionItem: React.FC<{
    question: PxQuestion;
    questions: PxQuestion[];
    dispatch: React.Dispatch<PxReportAction>;
    errorKeys?: Set<string>;
    depth: number;
    isDisabled?: boolean;
}> = ({ question, questions, dispatch, errorKeys, depth, isDisabled }) => {
    const selectedAnswer = question.answers.find((answer) => answer.isSelected);
    const selectedUid = selectedAnswer ? answerUidOf(selectedAnswer) : '';
    const isInvalid = Boolean(errorKeys?.has(question.key));

    // `isDisabled` am FormControl jeder Frage: Chakra reicht es über den Kontext an
    // Radio, Checkbox, Textarea und NumberInput darin weiter. An einem äußeren Container
    // allein würde es nicht greifen, weil dieses FormControl den Kontext neu setzt.
    return (
        <FormControl
            as="fieldset"
            isInvalid={isInvalid}
            isRequired={question.isMandatory}
            isDisabled={isDisabled}
        >
            <FormLabel as="legend" mb={2}>
                {question.question}
            </FormLabel>

            {/* Each answer's reveal (comment / quantity / follow-up questions)
                renders directly beneath the option it belongs to, so the
                dynamic content stays visually tied to the selection. */}
            {question.isMultipleAnswer ? (
                <VStack align="stretch" spacing={2}>
                    {question.answers.map((answer) => (
                        <Box key={answerUidOf(answer)}>
                            <Checkbox
                                isChecked={answer.isSelected}
                                onChange={() =>
                                    dispatch({
                                        type: 'SELECT_ANSWER',
                                        questionKey: question.key,
                                        answerUid: answerUidOf(answer),
                                    })
                                }
                            >
                                {answer.answer}
                            </Checkbox>
                            <PxAnswerHint hint={answer.hint} />
                            <PxAnswerReveals
                                question={question}
                                answer={answer}
                                questions={questions}
                                dispatch={dispatch}
                                errorKeys={errorKeys}
                                depth={depth}
                                isDisabled={isDisabled}
                            />
                        </Box>
                    ))}
                </VStack>
            ) : (
                <RadioGroup
                    value={selectedUid}
                    onChange={(answerUid) =>
                        dispatch({
                            type: 'SELECT_ANSWER',
                            questionKey: question.key,
                            answerUid,
                        })
                    }
                >
                    <VStack align="stretch" spacing={2}>
                        {question.answers.map((answer) => (
                            <Box key={answerUidOf(answer)}>
                                <Radio value={answerUidOf(answer)}>{answer.answer}</Radio>
                                <PxAnswerHint hint={answer.hint} />
                                <PxAnswerReveals
                                    question={question}
                                    answer={answer}
                                    questions={questions}
                                    dispatch={dispatch}
                                    errorKeys={errorKeys}
                                    depth={depth}
                                    isDisabled={isDisabled}
                                />
                            </Box>
                        ))}
                    </VStack>
                </RadioGroup>
            )}

            <FormErrorMessage>Pflichtfeld – bitte eine Option wählen.</FormErrorMessage>
        </FormControl>
    );
};

export const PxQuestionList: React.FC<PxQuestionListProps> = ({
    questions,
    questionKeys,
    dispatch,
    errorKeys,
    depth = 0,
    isDisabled,
}) => {
    const byKey = React.useMemo(() => {
        const map: Record<string, PxQuestion> = {};
        for (const question of questions) map[question.key] = question;
        return map;
    }, [questions]);

    // Resolve keys to questions, silently skipping dangling refs (dirty catalogs).
    const resolved = questionKeys
        .map((key) => byKey[key])
        .filter((question): question is PxQuestion => Boolean(question));

    if (resolved.length === 0) return null;

    return (
        <VStack
            align="stretch"
            spacing={5}
            borderLeftWidth={depth > 0 ? '2px' : undefined}
            borderColor="gray.200"
            pl={depth > 0 ? 4 : undefined}
        >
            {resolved.map((question) => (
                <PxQuestionItem
                    key={question.key}
                    question={question}
                    questions={questions}
                    dispatch={dispatch}
                    errorKeys={errorKeys}
                    depth={depth}
                    isDisabled={isDisabled}
                />
            ))}
        </VStack>
    );
};
